import { useDispatch, useSelector } from 'react-redux';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { GTM_QUEUE_STORAGE_KEY } from '@constants/constants';
import { baseApi } from '@/stores/baseApi';
import { graphqlBaseApi } from '@/stores/graphqlBaseApi';
import { rtkQueryErrorToastMessage } from '@/stores/rtkQueryErrorToastMessage';
import { preferencesSlice, preferencesMiddleware } from '@/stores/preferences';
import { contentApi } from '@/stores/contentApi';
import { productsListingSlice } from '@/pages/mijn-carel-lurvink/catalogus/stores/productsListingSlice';
import { activitysSlice, activityMiddleware } from '@/components/ActivityIndicator/activitySlice';
import { productSearchSlice } from '@/components/Search/stores/productSearchSlice';
import { gtmMiddleware } from '@/stores/gmt/gtmMiddleware';
import { createActionQueueMiddleware } from '@/stores/redux-action-queue-middleware';
import { createWrapper, type MakeStore } from './utils/store-wrapper';

import type { TypedUseSelectorHook } from 'react-redux';
import type { ListenerEffectAPI, TypedStartListening, TypedAddListener } from '@reduxjs/toolkit';

export const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  [activitysSlice.name]: activitysSlice.reducer,
  [productsListingSlice.name]: productsListingSlice.reducer,
  [preferencesSlice.name]: preferencesSlice.reducer,
  [contentApi.reducerPath]: contentApi.reducer,
  [productSearchSlice.name]: productSearchSlice.reducer
});

const makeStore: MakeStore<any> = (context: any) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: {
            cookies: context?.ctx?.req?.cookies || []
          }
        },
        immutableCheck: false,
        serializableCheck: false
      }).concat(
        baseApi.middleware,
        createActionQueueMiddleware(GTM_QUEUE_STORAGE_KEY),
        activityMiddleware.middleware,
        graphqlBaseApi.middleware,
        preferencesMiddleware.middleware,
        rtkQueryErrorToastMessage,
        gtmMiddleware.middleware
      ),
    devTools: true
  });
export type AppRootStore = ReturnType<typeof makeStore>;
export const wrapper = createWrapper<AppRootStore>(makeStore, { debug: false });

export type AppDispatch = AppRootStore['dispatch'];
export type AppRootState = ReturnType<typeof rootReducer>;
export type AppListenerEffectAPI = ListenerEffectAPI<AppRootState, AppDispatch>;
export type AppStartListening = TypedStartListening<AppRootState>;
export type AppAddListener = TypedAddListener<AppRootState>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<AppRootState> = useSelector;
